import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { nameBasedProtectedRoutes } from "../../../router/protected";

import { Card } from "react-bootstrap";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_SortingState,
} from "material-react-table";
import { datetimeFormats } from "../../../constants/constants";
import moment from "moment";
import { upcomingInspectorInspectionRequest } from "../../../services/dashboard";
import { getEncodedValue } from "../../../helpers/common";
import { GOOGLE_MAP_URL } from "../../../constants/paths";

/**
 * Create a custom component which will render upcoming inspection section in dashboard.
 * @param props
 * @returns Today's Inspection component
 */
export const UpcomingInspectionModule = (props: { userId: number }) => {
  const [sortColumn, setSortColumn] = useState("inspection_date");
  const [sortDirection, setSortDirection] = useState("asc");
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: sortColumn, desc: false },
  ]); // The default sorting data to show status column sort ico
  const [data, setData] = useState([]);
  useEffect(() => {
    fetchUpcomingInspections();
  }, [sorting]);

  const fetchUpcomingInspections = async () => {
    const param = {
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
    };
    upcomingInspectorInspectionRequest(param, props.userId).then((response) => {
      setData(response.inspectionResult);
    });
  };

  /* Column Definition requires for MaterialReactTable */
  type UpcommingInspection = {
    id: string;
    sr_number: string;
    address: string;
    city: string;
    zipcode: string;
    user: {
      full_name: string;
      company_name: string;
    };
    state: {
      title: string;
    };
    inspection_date: string;
    job_time: string;
    status: string;
  };

  // Mentioning table columns belows.
  const tableColumns = useMemo<MRT_ColumnDef<UpcommingInspection>[]>(
    () => [
      {
        accessorKey: "sr_number",
        header: "SR NO.",
        Cell: ({ cell, row }) => (
          <Link
            to={`${
              nameBasedProtectedRoutes.iInspectionDetail.path
            }/${getEncodedValue(row.original.id)}`}
            className="text-decoration-none text-link"
          >
            <strong>{cell.getValue<string>()}</strong>
          </Link>
        ),
      },
      {
        accessorKey: "user.company_name",
        header: "COMPANY",
      },
      {
        accessorKey: "user.full_name",
        header: "CUSTOMER",
      },
      {
        accessorKey: "address",
        header: "LOCATION",
        Cell: ({ cell, row }) => {
          const city = row.original?.city ? ", " + row.original?.city : "";
          const state = row.original?.state
            ? ", " + row.original?.state?.title
            : "";
          const zip = row.original?.zipcode ? ", " + row.original?.zipcode : "";
          const fullAddress = row.original.address + city + state + zip;
          return row.original.address ? (
            <Link
              target="_blank"
              to={`${GOOGLE_MAP_URL}${fullAddress}`}
              className="text-decoration-none text-link"
            >
              {fullAddress}
            </Link>
          ) : (
            ""
          );
        },
      },
      {
        accessorKey: "inspection_date",
        header: "INSPECTION DATE",
        Cell: ({ cell, row }) => {
          return row.original.status !== "pending"
            ? moment(
                `${row.original.inspection_date} ${row.original.job_time}`
              ).format(datetimeFormats.separatedDateTime)
            : "-";
        },
      },
    ],
    []
  );

  return (
    <div className="col-xl-12 mb-3">
      <Card.Header className="border-0 bg-default bg-theme black-color fs-20 fw-bold ls-2 br-0 pe-4 ps-4 pt-11">
        UPCOMING INSPECTIONS
      </Card.Header>
      <Card.Body className="br-10 p-4">
        <div className="table-responsive">
          <MaterialReactTable
            muiTableProps={{
              classes: {
                root: "table custom-table border-none-table table-striped",
              },
            }}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableDensityToggle={false}
            enableHiding={false}
            enableColumnFilters={false}
            enablePagination={false}
            columns={tableColumns}
            data={data}
            onSortingChange={setSorting}
            manualSorting
            state={{ sorting }}
          />
        </div>
      </Card.Body>
    </div>
  );
};
